import { postcodeValidator, postcodeValidatorExistsForCountry, } from "postcode-validator";
// Country-dependent checks for the postal code and city of an address. The
// country is the portal country (ISO 3166-1 alpha-2), since the address forms
// have no country field.
// Shown to the user as the expected format when a postal code does not match
// the pattern for the country. A country with a pattern but missing here gets
// a message without an example.
const POSTAL_CODE_EXAMPLES = {
    AU: "1234",
    CA: "A1A 1A1",
    DE: "12345",
    DK: "1234",
    EE: "12345",
    ES: "12345",
    FI: "12345",
    FR: "12345",
    GB: "SW1A 1AA",
    IS: "123",
    IT: "12345",
    MX: "12345",
    NA: "12345",
    NL: "1234 AB",
    NO: "1234",
    NZ: "1234",
    PL: "12-345",
    PT: "1234-567",
    SE: "123 45",
    US: "12345",
    ZA: "1234",
};
// Patterns used instead of the one postcode-validator has for the country.
// Namibia uses five digits, but most users there still type the older four
// digit codes, such as 9000 for Swakopmund.
const POSTAL_CODE_PATTERNS = {
    NA: /^\d{4,5}$/,
};
// Countries without a national postal code system, where any value is
// accepted. postcode-validator has no pattern for any of them. Users in these
// countries type the city or "none" into a required postal code field.
// prettier-ignore
const COUNTRIES_WITHOUT_POSTAL_CODES = [
    "AE", "AG", "AO", "AW", "BF", "BI", "BJ", "BO", "BQ", "BS", "BW", "BZ",
    "CD", "CF", "CG", "CI", "CM", "CW", "DJ", "DM", "ER", "FJ", "GA", "GD",
    "GH", "GM", "GQ", "GY", "HK", "JM", "KI", "KM", "KN", "KP", "LC", "LY",
    "ML", "MO", "MR", "MW", "NR", "NU", "QA", "RW", "SB", "SC", "SL", "SR",
    "SS", "ST", "SX", "SY", "TD", "TF", "TG", "TK", "TL", "TO", "TT", "TV",
    "UG", "VU", "YE", "ZW",
];
// Countries where a place name can be digits only, such as the town 1770 in
// Australia
const COUNTRIES_WITH_NUMERIC_CITY_NAMES = ["AU"];
const ILLEGAL_CHARACTERS = /[@*•●]/;
// A city made of digits and punctuation only is almost always a postal code
// typed into the wrong field. Checking for this instead of for letters keeps
// the check independent of the script the city is written in.
const NO_LETTERS = /^[\d\s.,'"/()#-]+$/;
export const POSTAL_CODE_MIN_LENGTH = 3;
export const POSTAL_CODE_MAX_LENGTH = 10;
const hasPostalCodes = (country) => COUNTRIES_WITHOUT_POSTAL_CODES.indexOf(country) === -1;
const hasFormat = (country) => country in POSTAL_CODE_PATTERNS ||
    postcodeValidatorExistsForCountry(country);
const matchesFormat = (postalCode, country) => country in POSTAL_CODE_PATTERNS
    ? POSTAL_CODE_PATTERNS[country].test(postalCode)
    : postcodeValidator(postalCode, country);
// Variants of a postal code to test against the country's pattern, in order
// of preference. They fix what users in the production data typed most:
// "2019." with a trailing dot, "N-7024" with a country prefix, and a
// separator that is missing, extra or of the wrong kind, such as "00950",
// "756-49" or "72 222".
function postalCodeCandidates(postalCode) {
    const withoutDot = postalCode.replace(/\.+$/, "");
    const withoutPrefix = withoutDot.replace(/^[A-Z]{1,3} ?- ?/, "");
    const candidates = [];
    for (const base of [postalCode, withoutDot, withoutPrefix]) {
        candidates.push(base);
        const bare = base.replace(/[\s.-]+/g, "");
        candidates.push(bare);
        for (const separator of ["-", " "]) {
            for (let i = 1; i < bare.length; i++) {
                candidates.push(bare.slice(0, i) + separator + bare.slice(i));
            }
        }
    }
    return candidates;
}
/**
 * Returns the postal code in the form the country's pattern accepts, such as
 * "00-950" for "00950" in Poland, with whitespace collapsed and letters in
 * upper case. Returns the value unchanged when no variant of it is valid, so
 * the user still sees what they typed next to the error.
 */
export function normalizePostalCode(value, countryCode) {
    var _a;
    const country = (_a = countryCode === null || countryCode === void 0 ? void 0 : countryCode.toUpperCase()) !== null && _a !== void 0 ? _a : "";
    // \s also matches the non-breaking space, which copied and autofilled
    // postal codes often contain
    const postalCode = value.replace(/\s+/g, " ").trim();
    if (!hasPostalCodes(country) || !hasFormat(country)) {
        return postalCode;
    }
    const candidates = postalCodeCandidates(postalCode.toUpperCase());
    for (const candidate of candidates) {
        if (matchesFormat(candidate, country)) {
            return candidate;
        }
    }
    return value;
}
/**
 * Returns null when the postal code is valid for the country, or can be made
 * valid by normalizePostalCode(). An empty value is a problem, as it always
 * was for postal codes, except in countries without postal codes; callers add
 * their own required message.
 */
export function validatePostalCode(value, countryCode) {
    var _a, _b;
    const country = (_a = countryCode === null || countryCode === void 0 ? void 0 : countryCode.toUpperCase()) !== null && _a !== void 0 ? _a : "";
    if (!hasPostalCodes(country)) {
        return null;
    }
    const postalCode = normalizePostalCode(value, country);
    if (hasFormat(country)) {
        return matchesFormat(postalCode, country)
            ? null
            : { kind: "format", example: (_b = POSTAL_CODE_EXAMPLES[country]) !== null && _b !== void 0 ? _b : null };
    }
    const allowedCharacters = new RegExp(`^[0-9A-Za-z\\s-]{${POSTAL_CODE_MIN_LENGTH},${POSTAL_CODE_MAX_LENGTH}}$`);
    if (!allowedCharacters.test(postalCode)) {
        return { kind: "characters" };
    }
    // No country uses postal codes of letters only
    if (!/\d/.test(postalCode)) {
        return { kind: "needsDigit" };
    }
    return null;
}
/**
 * A postal code that validatePostalCode() accepts for the country, for tests
 * that fill in an address on a portal of any country.
 */
export function examplePostalCode(countryCode) {
    return ((countryCode && POSTAL_CODE_EXAMPLES[countryCode.toUpperCase()]) || "12345");
}
/**
 * Returns null when the city is valid for the country. An empty value is
 * valid, so a city field is optional unless the caller makes it required.
 */
export function validateCity(value, countryCode) {
    var _a;
    const city = value.trim();
    if (city === "") {
        return null;
    }
    if (ILLEGAL_CHARACTERS.test(city)) {
        return { kind: "characters" };
    }
    const allowsNumericCity = COUNTRIES_WITH_NUMERIC_CITY_NAMES.indexOf((_a = countryCode === null || countryCode === void 0 ? void 0 : countryCode.toUpperCase()) !== null && _a !== void 0 ? _a : "") !== -1;
    if (!allowsNumericCity && NO_LETTERS.test(city)) {
        return { kind: "needsLetter" };
    }
    return null;
}
